/* global globalThis */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import uuidv4 from 'uuid/v4';
import { PopupTypes } from '../../../components/Popup';
import { T } from '../../../components/Translations';
import { ApiUrl, UserRoles } from '../../../project/Defines';
import { dispatchCustomEvent, EVENT_NAME, useCustomEvent } from '../../../project/utilities';
import InputCont from '../../../Shared/Form/InputCont';
import { ValidationDropDownValueList, ValidationInput } from '../../../Shared/Form/validation';
import ListView from '../../../Shared/ListView';
import { COLUMN_TYPE } from '../../../Shared/ListView/helper';
import useDetail from '../../../Shared/useDetail';
import { openPopup } from '../../../store/actions';
import { hasValidCallData, /*isDocumentReadOnly*/ isDocumentReadOnlyForFeesAndSpareParts, isLineReadOnly } from '../commonFunctions';

const listUpdateEvent = EVENT_NAME.SPARE_PART_LIST_UPDATE;
const reloadEvent = EVENT_NAME.SPARE_PART_DATA_RELOAD;

/**
 * the SparePartDetail
 * @exports SparePartDetail
 */
export default function SparePartDetail(props) {
    const dispatch = useDispatch();
    const isDispatcher = useSelector(s => s.role) === UserRoles.DISPATCHER;
    const { data } = props;
    const { windowKey, readOnly } = props.detailProps;
    const isServiceCallTabMode = !!props.detailProps?.isServiceCallTabMode;
    const [disableSaveButton, setDisableSaveButton] = useState(false);
    const isCreateNewMode = !data.DocNum;
    const { callData } = hasValidCallData(data) ? data : { callData: { call_id: data.SCOrigin } };

     const [isMobile, setIsMobile] = useState(globalThis.innerWidth <= 768);
  
    useEffect(() => {
        const handleResize = () => {
        setIsMobile(globalThis.innerWidth <= 768);
        };
        
        globalThis.addEventListener('resize', handleResize);
        return () => globalThis.removeEventListener('resize', handleResize);
    }, []);

    const onLoadData = useCallback((response, setDataItem) => {
        Array.isArray(response) && setDataItem(prev => ({
            ...(prev || data),
            callData: prev?.callData || data?.callData || callData,
            orderLines: response.map((m, i) => ({ ...m, key: uuidv4(), ID: uuidv4() }))
        }));
        return true;
    }, [data, callData]);

    const { dataItem, setDataItem, updateData, detailRef, closePopup } = useDetail({
        data: data.DocNum ? data : { ...data, orderLines: [] },
        updateUrl: ApiUrl.SparePartUpdate,
        dataLoadUrl: data.DocNum && ApiUrl.SparePartLineLoad,
        dataLoadParams: { docNum: data.DocNum, call_id: data.SCOrigin },
        additionalData: { callID: callData.ID },
        onLoadData,
        listUpdateEvent,
        windowKey,
        reloadEvent
    });

    const currentCallId = dataItem?.callData?.ID || data?.callData?.ID || callData?.ID;
    const currentCallCode = dataItem?.callData?.call_id || data?.callData?.call_id || callData?.call_id;

    useCustomEvent(EVENT_NAME.SERVICE_CALL_UPDATED, useCallback((event) => {
        const updatedCall = event?.detail?.data;
        const isSameById = updatedCall?.ID && currentCallId && String(updatedCall.ID) === String(currentCallId);
        const isSameByCallId = updatedCall?.call_id && currentCallCode && String(updatedCall.call_id) === String(currentCallCode);

        if (!isSameById && !isSameByCallId) {
            return;
        }

        setDataItem(prev => {
            if (!prev) {
                return prev;
            }

            const mergedCallData = prev.callData ? { ...prev.callData, ...updatedCall } : updatedCall;
            return {
                ...prev,
                callData: mergedCallData
            };
        });
    }, [currentCallId, currentCallCode, setDataItem]));

    const isReadOnlyMode = (!isServiceCallTabMode && readOnly) || isDocumentReadOnlyForFeesAndSpareParts({
        ...(dataItem || data),
        callData: dataItem?.callData || data?.callData || callData
    });

    const updateSuccessCallback = useCallback((response) => {
        if (detailRef.current) {
            closePopup(windowKey);
        }
        dispatchCustomEvent(listUpdateEvent);
    }, [detailRef, closePopup, windowKey]);

    const onCustomSave = useCallback(({ editingRecord, record, setEditingRecord, validation }) => {
        if (!(validation && !validation.isValid)) {
            setDataItem({
                ...dataItem, orderLines: dataItem.orderLines.map(ol => ol.key === editingRecord.key ? { ...editingRecord, ID: editingRecord.key } : ol)
            });
            setEditingRecord(null);
        }
    }, [dataItem, setDataItem]);

    const onCustomCancel = useCallback((setEditingRecord) => {
        setDataItem({
            ...dataItem, orderLines: [...dataItem.orderLines.filter(ol => ol.ID !== 'new')]
        });
        setEditingRecord(null);
    }, [dataItem, setDataItem]);

    const onRemove = useCallback((record) => {
        setDataItem({
            ...dataItem, orderLines: [...dataItem.orderLines.filter(ol => ol.ID !== record.ID)]
        });
    }, [dataItem, setDataItem]);

    const onNewClick = useCallback((event, onEdit) => {
        const newData = { key: uuidv4(), ID: 'new', Quantity: 1 };
        setDataItem({ ...dataItem, orderLines: [newData, ...dataItem.orderLines.filter(ol => ol.ID !== 'new')] });
        onEdit(newData, null, event, false);
    }, [dataItem, setDataItem]);

    const onCancelLine = line => {
        dispatch(
            openPopup({
                type: PopupTypes.Confirm,
                title: <T>text.cancel</T>,
                text: <T> message.delete_confirm</T>,
                buttonYesText: <T>text.yes</T>,
                buttonNoText: <T>text.no</T>,
                yesCallback: () => {
                    updateData((response) => {
                        dispatchCustomEvent(reloadEvent);
                        dispatchCustomEvent(listUpdateEvent);
                    }, null, { additionalData: { canceledLine: line.LineNum } });
                }

            }));
    }

    const commands = [];
    !isReadOnlyMode && commands.push({ command: 'edit', skipCommandForRecord: isLineReadOnly })
    !isReadOnlyMode && !isCreateNewMode && commands.push({ command: 'remove', tooltip: 'text.cancel', skipCommandForRecord: isLineReadOnly, onClick: onCancelLine })
    isCreateNewMode && commands.push({ command: 'delete', onClick: onRemove });

    const columns = [
        {
            text: 'text.spare_part',
            noSort: 'true',
            field: 'CodeCom',
            fixed: 'left',
            type: COLUMN_TYPE.TEXT,
            width: 200,
            render: (t, record) => {
                let text = [record.CodeCom, record.Description].filter(Boolean).join(' - ');
                return <wrap title={text} >
                    <text>{text}</text>
                </wrap>
            },
            readOnly: !isCreateNewMode,
            editor: {
                customRenderer: ({ editingRecord, setEditingRecord, validation }) => {
                    return <ValidationDropDownValueList
                        validation={validation}
                        name='CodeCom'
                        value={editingRecord.CodeCom}
                        valuelist='product_articles'
                        additionalData={callData.produit_uuid}
                        disableCach={true}
                        searchOptions={{ contains: true }}
                        onChange={(e, dataSource) => {
                            const item = dataSource.find(x => x.value === e);
                            setEditingRecord({ ...editingRecord, CodeCom: item.value, Description: item.description });
                        }}
                    />
                }
            }
        },
        {
            text: 'text.quantity',
            noSort: 'true',
            field: 'Quantity',
            width: 90,
            type: COLUMN_TYPE.TEXT,
            editor: {
                customRenderer: ({ editingRecord, setEditingRecord, validation }) => {
                    return <ValidationInput
                        validation={validation}
                        name='Quantity'
                        type='number'
                        inputType='number'
                        min={1}
                        className='ant-input'
                        value={editingRecord.Quantity}
                        onChange={(e = 1) => {
                            let quantity = e; /** //e.target.value; */
                            setEditingRecord({ ...editingRecord, Quantity: quantity });
                        }}
                    />
                }
            }
        },
        {
            readOnly: true,
            text: 'text.status',
            noSort: 'true',
            width: 120,
            field: 'LineStatus',
            type: COLUMN_TYPE.TEXT
        },
        {
            skip: !isDispatcher,
            readOnly: true,
            text: 'text.account',
            noSort: 'true',
            width: 120,
            field: 'Account',
            type: COLUMN_TYPE.TEXT
        },
        {
            skip: !isDispatcher,
            readOnly: true,
            text: 'text.business_unit',
            noSort: 'true',
            width: 120,
            field: 'BusinessUnit',
            type: COLUMN_TYPE.TEXT
        },
        {
            skip: !isDispatcher,
            readOnly: true,
            text: 'text.cost_center',
            noSort: 'true',
            width: 120,
            field: 'CostCenter',
            type: COLUMN_TYPE.TEXT
        },
        {
            skip: !isDispatcher,
            readOnly: true,
            text: 'text.destination',
            noSort: 'true',
            width: 120,
            field: 'Destination',
            type: COLUMN_TYPE.TEXT
        },
        {
            type: COLUMN_TYPE.ACTION,
            width: 100,
            commands,
            onCustomSave,
            onCustomCancel
        }
    ].filter(c => !c.skip);

    return (dataItem?.orderLines && < div className="form_container"
        >
        <view  >
            <form>
                {isMobile ? 
                    <>
                        <InputCont
                            label='text.call_id'
                            inputProps={{ disabled: true, name: 'call_id', value: callData.call_id }}
                        />
                        <InputCont
                            label='text.customer_code'
                            inputProps={{ disabled: true, name: 'code_client', value: dataItem.CardCode || callData.code_client }}
                        />
                        <InputCont
                            label='text.request_id'
                            inputProps={{ disabled: true, name: 'DocNum', value: dataItem.DocNum }} />
                    </> :
                    <toolbar>
                        <InputCont
                            label='text.call_id'
                            inputProps={{ disabled: true, name: 'call_id', value: callData.call_id }}
                        />
                        <InputCont
                            label='text.customer_code'
                            inputProps={{ disabled: true, name: 'code_client', value: dataItem.CardCode || callData.code_client }}
                        />
                        <InputCont
                            label='text.request_id'
                            inputProps={{ disabled: true, name: 'DocNum', value: dataItem.DocNum }} />
                    </toolbar>
                }
                <ListView
                    {...props}
                    columnConfig={columns}
                    className={`scroll_fix ${isMobile ? "visible_buttons" : ""}`}
                    noPaging
                    keyField={isCreateNewMode ? "ID" : "LineNum"}
                    dataSource={dataItem.orderLines}
                    validationFields={['CodeCom', { name: 'Quantity', rules: [{ type: "number" }] }]}
                    editable={!isReadOnlyMode}
                    isChieldView
                    onNewClick={onNewClick}
                    onEditingRecordChange={editingRecord => { setDisableSaveButton(editingRecord !== null); }}
                    skipNewButton={!isCreateNewMode || isReadOnlyMode}
                    skipEdit={isReadOnlyMode}
                    isRecordReadOnly={isLineReadOnly}
                />
                {(() => {
                    const canSave = !disableSaveButton && dataItem.orderLines?.some(l => !isLineReadOnly(l));
                    return isReadOnlyMode ? <></> : <toolbar>
                        <wrap>
                            <action>
                                <button
                                    type='button'
                                    disabled={!canSave}
                                    onClick={() => updateData(updateSuccessCallback)}
                                    className='button'
                                ><icon>save</icon><T>text.save</T></button>
                            </action>
                        </wrap>
                    </toolbar>;
                })()}

            </form>
        </view >

    </div >) || <></>;
}

SparePartDetail.propTypes = {
    data: PropTypes.object.isRequired,
    detailProps: PropTypes.shape({
        windowKey: PropTypes.string.isRequired,
        readOnly: PropTypes.bool,
        isServiceCallTabMode: PropTypes.bool
    }).isRequired
};